/**
 * What the server writes for players (ADR 0069, step 2): HTTP refusals, the
 * validation lines of the shared schemas, and the announcements of the rooms.
 * Fixed lines are keyed by their French; a line with values by its French
 * template (`key` of the message), filled on the client. The admins' tools
 * (GM window, moderation, map editor) stay in French.
 */
export const SERVER: Readonly<Record<string, string>> = {
  // auth/account-service.ts, auth/guest-service.ts
  'Cette adresse e-mail est déjà utilisée': 'This email address is already in use',
  'Ce compte a déjà un e-mail et un mot de passe':
    'This account already has an email and a password',
  'E-mail ou mot de passe incorrect': 'Wrong email or password',
  'Ce personnage ne t’appartient pas': 'This character isn’t yours',
  'Un compte a au plus {count} personnages': 'An account holds at most {count} characters',
  'Le nom ne correspond pas au personnage': 'The name doesn’t match the character',
  'Un de tes personnages est encore en jeu : attends quelques secondes et réessaie':
    'One of your characters is still in the game: wait a few seconds and try again.',
  'Ce personnage est en jeu : quitte-le avant de le supprimer':
    'This character is in the game: leave it before deleting it',
  'Ce personnage a reçu des achats de la boutique : il ne peut pas être supprimé':
    'This character received Shop purchases: it can’t be deleted',
  'Le pseudo « {name} » est déjà pris': 'The nickname “{name}” is already taken',
  'Ce pseudo n’est pas autorisé': 'This nickname isn’t allowed',

  // auth/identity-service.ts
  'Un compte existe déjà avec cette adresse : connecte-toi avec ton mot de passe, puis lie ce service depuis le jeu.':
    'An account already uses this address: log in with your password, then link this service from the game.',
  'Ce compte est déjà lié à un autre compte Shefira.':
    'This account is already linked to another Shefira account.',
  'Ton compte est déjà lié à un autre compte de ce service.':
    'Your account is already linked to another account of this service.',

  // auth/password-reset.ts, http/password-reset.ts
  'Ce lien n’est plus valable : demande-en un nouveau.':
    'This link is no longer valid: ask for a new one.',
  'La réinitialisation par e-mail est indisponible : écris à contact@shefira.com.':
    'Email reset is unavailable: write to contact@shefira.com.',
  'Adresse e-mail invalide': 'Invalid email address',

  // http/account.ts, http/auth.ts, shared messages/auth.ts (validation)
  'Données invalides': 'Invalid data',
  'Pseudo ou classe invalide': 'Invalid nickname or class',
  'Le pseudo fait au moins 2 caractères': 'The nickname needs at least 2 characters',
  'Le pseudo fait au plus 16 caractères': 'The nickname has at most 16 characters',
  'Lettres et chiffres, avec - ou _ à l’intérieur': 'Letters and digits, with - or _ inside',
  'Le mot de passe fait au moins 8 caractères': 'The password needs at least 8 characters',
  'Le mot de passe fait au plus 128 caractères': 'The password has at most 128 characters',

  // http/rate-limit.ts
  'Trop de requêtes, réessaie dans quelques secondes.':
    'Too many requests, try again in a few seconds.',

  // http/sign-in.ts
  'La connexion Google n’est pas disponible.': 'Google sign-in is not available.',
  'La connexion a expiré : recommence.': 'The sign-in expired: start again.',
  'Connexion annulée.': 'Sign-in canceled.',
  '{service} ne répond pas : réessaie dans un instant.':
    '{service} isn’t answering: try again in a moment.',
  'Le service n’a pas confirmé ton identité.': 'The service didn’t confirm who you are.',

  // http/shop.ts
  'Cet article n’existe pas.': 'This item does not exist.',
  'Cette offre n’est plus en vente pour cette saison.':
    'This offer is no longer on sale this season.',
  'Cette commande n’existe pas.': 'This order does not exist.',
  'La boutique est fermée pour le moment.': 'The Shop is closed for now.',
  'Cette commande n’est pas à récupérer.': 'This order has nothing to collect.',
  'Ton sac est plein.': 'Your Bag is full.',
  'Coche la demande de livraison immédiate et de renonciation au droit de rétractation.':
    'Tick the box asking for immediate delivery and waiving your right of withdrawal.',
  'Enregistre ton compte (e-mail, Google ou Steam) pour acheter : un compte invité peut se perdre.':
    'Register your Account (email, Google or Steam) to buy: a Guest account can be lost.',
  'Le paiement est indisponible, réessaie plus tard.': 'Payment is unavailable, try again later.',

  // http/gift-codes.ts
  'Ce code n’existe pas.': 'This code does not exist.',
  'Ce code a expiré.': 'This code has expired.',
  'Ce code a déjà été utilisé autant de fois que prévu.':
    'This code has already been used as many times as planned.',
  'Ton compte a déjà reçu ce cadeau.': 'Your Account already received this gift.',
  'Ton sac est plein : fais de la place, puis réessaie.':
    'Your Bag is full: make room, then try again.',

  // http/auction.ts
  'Cet objet n’est pas dans ton sac.': 'This item isn’t in your Bag.',
  'Une pièce liée ne se vend pas.': 'A bound piece can’t be sold.',
  'Un achat de la boutique ne se vend pas avant quatorze jours.':
    'A Shop purchase can’t be sold for fourteen days.',
  'Tu n’en as pas autant.': 'You don’t have that many.',
  'Tu as déjà dix lots en vente.': 'You already have ten lots for sale.',
  'Ce lot n’existe pas.': 'This lot does not exist.',
  'Ce lot n’est plus en vente.': 'This lot is no longer for sale.',
  'C’est ton propre lot.': 'That’s your own lot.',
  'Tu n’as pas assez d’or.': 'You don’t have enough gold.',
  'Ce lot n’est pas le tien.': 'This lot isn’t yours.',
  'Ce lot n’est pas à récupérer.': 'This lot has nothing to collect.',
  'Ce lot appartient à un autre hôtel des ventes.': 'This lot belongs to another Auction House.',
  'Approche-toi d’un hôtel des ventes.': 'Get closer to an Auction House.',

  // rooms/zone-room.ts (announcements to one player)
  'Termine les leçons du camp avant de rejoindre Havrebourg : le forgeron rend la dernière.':
    'Finish the camp’s lessons before heading to Havrebourg: the Blacksmith gives the last one.',
  'Tu es tombé pendant l’invasion : tes points restent acquis, mais tu ne peux plus aider à défendre le village.':
    'You fell during the Invasion: you keep your points, but you can no longer help defend the village.',

  // shared rules/upgrade.ts (announcement)
  "{player} vient d'obtenir {item}": '{player} just got {item}',
  "{player} vient d'obtenir {item} +{level}": '{player} just got {item} +{level}',

  // shared rules/boss.ts (announcements)
  "{boss} s'est éveillé : {lair}": '{boss} has awoken: {lair}',
  '{boss} est tombé': '{boss} has fallen',
  // rooms/encounter-director.ts (ADR 0093)
  'Un colporteur s’est arrêté sur la route, non loin d’ici.':
    'A peddler has stopped on the road, not far from here.',
  'Au secours ! Des pillards attaquent une voyageuse sur la route !':
    'Help! Raiders are attacking a traveller on the road!',
  'Un coffre est tombé d’une charrette sur la route, non loin d’ici.':
    'A chest fell off a cart on the road, not far from here.',
  'La voyageuse te remercie et reprend la route.': 'The traveller thanks you and goes on her way.',
  '{boss} a été vaincu par {player}': '{boss} was defeated by {player}',
  '{boss} a été vaincu par {names} et {last}': '{boss} was defeated by {names} and {last}',

  // shared rules/rare-mobs.ts (announcements to a region)
  'On dit que {rare}, {epithet}, rôde dans la région : {region}':
    'Word is that {rare}, {epithet}, roams the region: {region}',
  '{rare} a disparu sans laisser de trace': '{rare} vanished without a trace',

  // shared rules/world-events.ts (announcements)
  '{event} : des gobelins marchent sur {village} (niv. {band}) dans moins d’une minute ! Tous à {village} !':
    '{event}: Goblins march on {village} (Lv. {band}) in less than a minute! Everyone to {village}!',
  '{event} : des gobelins marchent sur {village} (niv. {band}) dans une minute ! Tous à {village} !':
    '{event}: Goblins march on {village} (Lv. {band}) in one minute! Everyone to {village}!',
  '{event} : des gobelins marchent sur {village} (niv. {band}) dans {minutes} minutes ! Tous à {village} !':
    '{event}: Goblins march on {village} (Lv. {band}) in {minutes} minutes! Everyone to {village}!',
  '{village} est attaqué ! Repoussez les gobelins avant que le village ne tombe.':
    '{village} is under attack! Drive the Goblins back before the village falls.',
  'Les gobelins fuient {village} : le village est sauvé !':
    'The Goblins flee {village}: the village is saved!',
  '{village} est sauvé ! Premiers défenseurs : {podium}.':
    '{village} is saved! Top defenders: {podium}.',
  'Personne n’est venu défendre {village} : les gobelins ont rebroussé chemin, l’invasion est annulée.':
    'Nobody came to defend {village}: the Goblins turned back, the Invasion is called off.',
  'Les gobelins ont pillé {village} et repartent chargés de butin.':
    'The Goblins looted {village} and leave loaded with loot.',
  '{event} : un géant arrive aux abords de {village} (niv. {band}) dans {minutes} minutes ! Tous à {village} !':
    '{event}: a giant reaches the outskirts of {village} (Lv. {band}) in {minutes} minutes! Everyone to {village}!',
  '{event} : un géant arrive aux abords de {village} (niv. {band}) dans une minute ! Tous à {village} !':
    '{event}: a giant reaches the outskirts of {village} (Lv. {band}) in one minute! Everyone to {village}!',
  '{event} : un géant arrive aux abords de {village} (niv. {band}) dans moins d’une minute ! Tous à {village} !':
    '{event}: a giant reaches the outskirts of {village} (Lv. {band}) in less than a minute! Everyone to {village}!',
  '{event} : un chariot d’or part de {capital} pour {village} (niv. {band}) dans {minutes} minutes ! Escortez-le !':
    '{event}: a gold cart leaves {capital} for {village} (Lv. {band}) in {minutes} minutes! Escort it!',
  '{event} : un chariot d’or part de {capital} pour {village} (niv. {band}) dans une minute ! Escortez-le !':
    '{event}: a gold cart leaves {capital} for {village} (Lv. {band}) in one minute! Escort it!',
  '{event} : un chariot d’or part de {capital} pour {village} (niv. {band}) dans moins d’une minute ! Escortez-le !':
    '{event}: a gold cart leaves {capital} for {village} (Lv. {band}) in less than a minute! Escort it!',
  'Un géant rôde aux abords de {village} ! Abattez-le avant qu’il ne reparte.':
    'A giant prowls around {village}! Bring it down before it leaves.',
  'Le chariot d’or quitte {capital} pour {village} ! Restez près de lui : il n’avance pas sans escorte.':
    'The gold cart leaves {capital} for {village}! Stay close: it won’t move without an escort.',
  'Le géant de {village} est tombé !': 'The giant of {village} has fallen!',
  'Le géant de {village} est tombé ! Premiers combattants : {podium}.':
    'The giant of {village} has fallen! Top fighters: {podium}.',
  'Le chariot d’or est arrivé à {village} !': 'The gold cart reached {village}!',
  'Le chariot d’or est arrivé à {village} ! Premiers escorteurs : {podium}.':
    'The gold cart reached {village}! Top escorts: {podium}.',
  'Personne n’est venu affronter le géant de {village} : il est reparti, l’événement est annulé.':
    'Nobody came to face the giant of {village}: it left, the event is called off.',
  'Personne n’est venu escorter le chariot d’or vers {village} : il reste à {capital}, l’événement est annulé.':
    'Nobody came to escort the gold cart to {village}: it stays in {capital}, the event is called off.',
  'Le géant s’éloigne de {village}, invaincu.': 'The giant wanders away from {village}, unbeaten.',
  'Le chariot d’or n’a pas atteint {village} à temps : les gobelins l’ont pillé.':
    'The gold cart did not reach {village} in time: the Goblins looted it.',

  // rooms/achievement-desk.ts (announcement)
  '{player} a accompli « {achievement} » et devient {title} !':
    '{player} completed “{achievement}” and becomes {title}!',
};
